/**
 * chart_controller.js — Marketing Intelligence projection chart (Stimulus).
 *
 * Ports the former static/js/marketing_dashboard.js IIFE to an element-scoped
 * controller. The dashboard is a single interactive Chart.js view (actuals /
 * signups / baseline / claimed / optimistic) with horizon buttons, scenario
 * selection, drift overlay, snapshot save/compare, growth-rate editing and a
 * per-channel preview overlay. All of that re-renders one canvas.
 *
 * Lifecycle (replaces the old attachOnce() once-guard):
 *   connect()    binds the delegated document listeners and kicks off the first
 *                render; disconnect() removes those same listeners and destroys
 *                the chart. Because the controller instance owns add+remove, the
 *                listeners cannot stack across Turbo navigations — which is the
 *                exact failure attachOnce() was hand-rolling around.
 *   Chart.js is a page-scoped *body* script (vendor/chartjs/chart.js in the
 *                template's extra_js), so on a Turbo navigation `Chart` may not
 *                be defined when this controller connects. We defer the first
 *                render to turbo:load when it's missing — the standard defer-
 *                to-turbo:load pattern for body vendors. The control listeners bind
 *                immediately regardless (they don't need Chart).
 *   Chart teardown is delegated to window.TurboChart (turbo-utils.js), which
 *                owns destroy-before-recreate AND the global turbo:before-cache
 *                teardown, so cached snapshots ship no live canvas. We still
 *                TurboChart.destroy() on disconnect for non-cache removals
 *                (e.g. a Turbo Stream replace of the element).
 *
 * data-controller="chart" hosts on #marketingProjectionCard, which already
 * carries the API URLs (data-api-url, data-scenarios-url, data-history-url,
 * data-snapshot-url, data-initial-horizon) the controller reads.
 *
 * Design ref: docs/marketing/LIVE_PROJECTIONS_DESIGN.md §§ 7, 11, 16 Phase 3
 * Template:   templates/admin/marketing/marketing_dashboard.html
 */

import { Controller } from "@hotwired/stimulus";

    function $(sel, root) { return (root || document).querySelector(sel); }
    function $$(sel, root) { return (root || document).querySelectorAll(sel); }

    function formatPercent(v) {
        if (v === null || v === undefined) return '—';
        return Math.round(v * 100) + '%';
    }

    function formatNumber(v) {
        if (v === null || v === undefined) return '—';
        return Math.round(Number(v)).toLocaleString();
    }

export default class extends Controller {
        connect() {
            this.currentHorizon = 90;
            this.currentData = null;
            this.currentScenarioCodes = '';  // CSV for API, '' means no filter
            this.scenariosLoaded = false;
            this.previewSelected = new Set();
            this.scenarioPreviewState = {};  // preview selections per scenario

            // Drift overlay: faded historical claimed projections for comparison.
            this.driftEnabled = false;
            this.signupsVisible = true;
            this.driftSnapshots = null;      // Array<{edit_date, actor, labels, claimed}>
            this.driftCacheHorizon = null;   // Horizon the cached snapshots were for

            this._bindListeners();

            // Chart.js (Chart) is a page body script; on a Turbo nav it may not
            // be defined yet. Defer the first render to turbo:load when missing.
            if (typeof Chart !== 'undefined') {
                this._initData();
                return;
            }
            this._onLoad = () => this._initData();
            document.addEventListener('turbo:load', this._onLoad, { once: true });
        }

        disconnect() {
            if (this._onLoad) {
                document.removeEventListener('turbo:load', this._onLoad);
                this._onLoad = null;
            }
            if (this._listeners) {
                this._listeners.forEach(([evt, fn]) => document.removeEventListener(evt, fn));
                this._listeners = null;
            }
            if (window.TurboChart) window.TurboChart.destroy('marketingProjectionChart');
        }

        _bindListeners() {
            // Delegated on document because controls (modals appended to <body>)
            // live outside this.element. The controller instance owns add/remove,
            // so they don't stack across navigations (the attachOnce replacement).
            this._listeners = [
                ['click', this.onHorizonClick.bind(this)],
                ['click', this.onGrowthRateAction.bind(this)],
                ['click', this.onDriftToggle.bind(this)],
                ['click', this.onSignupsToggle.bind(this)],
                ['click', this.onSnapshotClick.bind(this)],
                ['click', this.onSaveSnapshotConfirmClick.bind(this)],
                ['click', this.onCompareClick.bind(this)],
                ['click', this.onCompareConfirmClick.bind(this)],
                ['change', this.onPreviewToggle.bind(this)],
                ['change', this.onSelectAllToggle.bind(this)],
                ['change', this.onScenarioChange.bind(this)],
            ];
            this._listeners.forEach(([evt, fn]) => document.addEventListener(evt, fn));
        }

        _initData() {
            this._onLoad = null;
            const card = document.getElementById('marketingProjectionCard');
            if (!card) return;
            const initialHorizon = parseInt(card.dataset.initialHorizon, 10);
            if (initialHorizon) this.currentHorizon = initialHorizon;

            const payload = this.readInitialPayload();
            if (payload) this.renderChart(payload);
            else this.fetchAndRender(this.currentHorizon);

            this.loadScenarios();
        }

        // ---- data readers ---------------------------------------------------

        readInitialPayload() {
            const tag = document.getElementById('marketing-snapshot-data');
            if (!tag) return null;
            try {
                return JSON.parse(tag.textContent);
            } catch (e) {
                console.error('[MarketingDashboard] bad initial payload', e);
                return null;
            }
        }

        // ---- meta / mobile summary ------------------------------------------

        updateMeta(meta) {
            if (!meta) return;
            const conv = $('#metaConversion');
            const growth = $('#metaGrowth');
            const claimedMid = $('#metaClaimedMid');
            const optMid = $('#metaOptMid');
            if (conv) conv.textContent = formatPercent(meta.conversion_rate);
            if (growth) growth.textContent = formatPercent(meta.daily_growth_rate);
            if (claimedMid) claimedMid.textContent = formatNumber(meta.claimed_midpoint_total);
            if (optMid) optMid.textContent = formatNumber(meta.optimistic_midpoint_total);
        }

        updateMobileSummary(data) {
            if (!data || !data.meta) return;

            const lastActuals = data.actuals.filter(v => v !== null);
            const lastSignups = (data.signups || []).filter(v => v !== null);
            const lastClaimed = data.claimed.filter(v => v !== null);
            const lastOptimistic = data.optimistic.filter(v => v !== null);

            const mobileBaseline = document.getElementById('mobileBaseline');
            const mobileSignupsToday = document.getElementById('mobileSignupsToday');
            const mobilePaidToday = document.getElementById('mobilePaidToday');
            const mobileClaimed = document.getElementById('mobileClaimed');
            const mobileOptimistic = document.getElementById('mobileOptimistic');

            if (mobileBaseline) {
                mobileBaseline.textContent = lastActuals.length > 0
                    ? formatNumber(lastActuals[lastActuals.length - 1])
                    : '—';
            }
            if (mobileSignupsToday) {
                mobileSignupsToday.textContent = lastSignups.length > 0
                    ? formatNumber(lastSignups[lastSignups.length - 1])
                    : '—';
            }
            if (mobilePaidToday) {
                mobilePaidToday.textContent = lastActuals.length > 0
                    ? formatNumber(lastActuals[lastActuals.length - 1])
                    : '—';
            }
            if (mobileClaimed) {
                mobileClaimed.textContent = lastClaimed.length > 0
                    ? formatNumber(lastClaimed[lastClaimed.length - 1])
                    : '—';
            }
            if (mobileOptimistic) {
                mobileOptimistic.textContent = lastOptimistic.length > 0
                    ? formatNumber(lastOptimistic[lastOptimistic.length - 1])
                    : '—';
            }
        }

        // ---- chart build / render -------------------------------------------

        buildDatasets(data) {
            const sets = [
                {
                    label: 'Actuals',
                    data: data.actuals,
                    borderColor: 'rgba(13,110,253,1)',
                    backgroundColor: 'rgba(13,110,253,0.1)',
                    borderWidth: 2,
                    spanGaps: false,
                    tension: 0.2,
                },
                {
                    label: 'Signups',
                    data: data.signups || [],
                    borderColor: 'rgba(214,51,132,0.95)',
                    backgroundColor: 'rgba(214,51,132,0.05)',
                    borderWidth: 2,
                    pointRadius: 0,
                    spanGaps: false,
                    tension: 0.2,
                    fill: false,
                    hidden: !this.signupsVisible,
                },
                {
                    label: 'Baseline',
                    data: data.baseline,
                    borderColor: 'rgba(108,117,125,0.9)',
                    borderDash: [4, 4],
                    borderWidth: 2,
                    pointRadius: 0,
                    spanGaps: true,
                },
                {
                    label: 'Claimed',
                    data: data.claimed,
                    borderColor: 'rgba(25,135,84,0.95)',
                    borderDash: [8, 4],
                    borderWidth: 2,
                    pointRadius: 0,
                    spanGaps: true,
                },
                {
                    label: 'Unclaimed Potential',
                    data: data.optimistic,
                    borderColor: 'rgba(253,126,20,0.95)',
                    borderDash: [2, 2],
                    borderWidth: 2,
                    pointRadius: 0,
                    spanGaps: true,
                },
            ];

            // Preview overlay: each day's new subs compound at daily_growth_rate for remaining days.
            // Matches backend: per_day * ((1+r)^k - 1) / r.
            if (this.previewSelected.size > 0 && data.baseline && data.meta) {
                let midpointSum = 0;
                this.previewSelected.forEach(code => {
                    const card = document.querySelector(`.channel-preview-card[data-channel-code="${code}"]`);
                    if (!card) return;
                    const lo = parseFloat(card.dataset.projLow) || 0;
                    const hi = parseFloat(card.dataset.projHigh) || 0;
                    midpointSum += (lo + hi) / 2.0;
                });
                const horizon = data.meta.horizon_days || this.currentHorizon;
                const r = data.meta.daily_growth_rate || 0;
                const conversionRate = data.meta.conversion_rate || 0;
                const delta = midpointSum * conversionRate;
                const perDay = delta / horizon;
                const futureIndex = data.baseline.findIndex(v => v !== null);
                const previewSeries = data.baseline.map((bv, idx) => {
                    if (bv === null) return null;
                    const k = idx - futureIndex + 1;
                    const channelGrowth = r > 0 ? (Math.pow(1 + r, k) - 1) / r : k;
                    return Math.round(bv + perDay * channelGrowth);
                });
                sets.push({
                    label: `Preview (${this.previewSelected.size} selected)`,
                    data: previewSeries,
                    borderColor: 'rgba(111,66,193,0.95)',
                    backgroundColor: 'rgba(111,66,193,0.1)',
                    borderDash: [12, 3],
                    borderWidth: 3,
                    pointRadius: 0,
                    spanGaps: true,
                });
            }

            if (this.driftEnabled && Array.isArray(this.driftSnapshots) && this.driftSnapshots.length > 0) {
                // Each past projection edit becomes one faded dataset, aligned to the master labels
                // by date string. Pad with nulls outside the snapshot's range.
                const labelIndex = new Map();
                data.labels.forEach((lbl, i) => labelIndex.set(lbl, i));
                this.driftSnapshots.forEach(snap => {
                    const series = new Array(data.labels.length).fill(null);
                    for (let i = 0; i < snap.labels.length; i++) {
                        const idx = labelIndex.get(snap.labels[i]);
                        if (idx !== undefined) series[idx] = snap.claimed[i];
                    }
                    sets.push({
                        label: `Claimed @ ${snap.edit_date}`,
                        data: series,
                        borderColor: 'rgba(25,135,84,0.22)',
                        borderDash: [2, 3],
                        borderWidth: 1,
                        pointRadius: 0,
                        spanGaps: true,
                        isDrift: true,
                    });
                });
            }
            return sets;
        }

        renderChart(data) {
            if (!data) return;
            this.currentData = data;
            this.updateMeta(data.meta);

            this.updateMobileSummary(data);

            const canvas = document.getElementById('marketingProjectionChart');
            if (!canvas || typeof Chart === 'undefined' || !window.TurboChart) return;

            const cfg = {
                type: 'line',
                data: { labels: data.labels, datasets: this.buildDatasets(data) },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                generateLabels: function(chart) {
                                    return chart.data.datasets
                                        .map((ds, i) => ({ ds, i }))
                                        .filter(({ ds }) => !ds.isDrift)
                                        .map(({ ds, i }) => ({
                                            text: ds.label,
                                            fillStyle: ds.borderColor,
                                            lineWidth: 2,
                                            strokeStyle: ds.borderColor,
                                            hidden: !chart.isDatasetVisible(i),
                                            datasetIndex: i,
                                        }));
                                }
                            }
                        },
                        tooltip: {
                            enabled: true,
                            callbacks: {
                                label: function(context) {
                                    const label = context.dataset.label || '';
                                    const value = context.parsed.y;
                                    if (value === null) return label;
                                    const unit = label === 'Signups' ? 'signups' : 'subscribers';
                                    return `${label}: ${formatNumber(value)} ${unit}`;
                                },
                                title: function(context) {
                                    if (!context[0]) return '';
                                    const label = context[0].label || '';
                                    if (!label) return '';
                                    const date = new Date(label);
                                    return date.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
                                }
                            }
                        },
                    },
                    scales: {
                        x: { ticks: { maxTicksLimit: 12 } },
                        y: { beginAtZero: false, title: { display: true, text: 'Paid subscribers' } },
                    },
                },
            };

            // TurboChart owns destroy-before-recreate + the global before-cache
            // teardown, so Chart.js never throws "Canvas is already in use" on a
            // Turbo restoration visit and cached snapshots ship no live canvas.
            window.TurboChart.mount('marketingProjectionChart', cfg);
        }

        refreshPreviewOverlay() {
            if (!this.currentData) return;
            this.renderChart(this.currentData);
        }

        async fetchAndRender(horizon) {
            const card = document.getElementById('marketingProjectionCard');
            if (!card) return;
            const apiUrl = card.dataset.apiUrl;
            if (!apiUrl) return;
            const url = new URL(apiUrl, window.location.origin);
            url.searchParams.set('days', String(horizon));
            if (this.currentScenarioCodes) {
                url.searchParams.set('channels', this.currentScenarioCodes);
            }

            const canvas = document.getElementById('marketingProjectionChart');
            if (canvas) canvas.style.opacity = '0.5';

            try {
                const data = await window.api(url.toString(), {
                    headers: { 'Accept': 'application/json' },
                    notifyError: false
                });
                this.renderChart(data);
            } catch (err) {
                console.error('[MarketingDashboard] fetch failed', err);
                const errorMsg = document.getElementById('chartError') || document.createElement('div');
                if (!document.getElementById('chartError')) {
                    errorMsg.id = 'chartError';
                    errorMsg.className = 'alert alert-danger mt-2';
                    errorMsg.innerHTML = '<i class="bi bi-exclamation-triangle me-2"></i><span>Failed to load projection data. Please try again.</span>';
                    const parent = canvas?.parentNode || card;
                    if (parent) parent.insertBefore(errorMsg, canvas?.nextSibling || parent.firstChild);
                }
            } finally {
                if (canvas) canvas.style.opacity = '1';
            }
        }

        async loadScenarios() {
            if (this.scenariosLoaded) return;
            const card = document.getElementById('marketingProjectionCard');
            if (!card) return;
            const url = card.dataset.scenariosUrl;
            const select = document.getElementById('scenarioSelect');
            if (!url || !select) return;
            try {
                const data = await window.api(url, {
                    headers: { 'Accept': 'application/json' },
                    notifyError: false
                });
                const scenarios = data.scenarios || [];
                // Populate (skip if already done — idempotent on turbo:load)
                // Preserve the first placeholder option.
                while (select.options.length > 1) select.remove(1);
                scenarios.forEach(s => {
                    const opt = document.createElement('option');
                    opt.value = (s.channel_codes || []).join(',');
                    opt.textContent = s.name + ' (' + (s.channel_codes || []).length + ')';
                    opt.dataset.scenarioId = s.id;
                    select.appendChild(opt);
                });
                this.scenariosLoaded = true;
            } catch (err) {
                console.warn('[MarketingDashboard] scenarios load failed', err);
            }
        }

        // ---- control handlers -----------------------------------------------

        onScenarioChange(event) {
            const select = event.target;
            if (select.id !== 'scenarioSelect') return;

            // Save current preview state before switching
            this.scenarioPreviewState[this.currentScenarioCodes] = new Set(this.previewSelected);

            const newCodes = select.value || '';
            this.currentScenarioCodes = newCodes;

            // Restore preview state for the new scenario
            const savedState = this.scenarioPreviewState[newCodes];
            if (savedState) {
                this.previewSelected.clear();
                savedState.forEach(code => this.previewSelected.add(code));
                // Sync UI checkboxes
                $$('.preview-toggle').forEach(input => {
                    input.checked = this.previewSelected.has(input.dataset.channelCode);
                });
            } else {
                this.previewSelected.clear();
                $$('.preview-toggle').forEach(input => {
                    input.checked = false;
                });
            }

            this.syncSelectAllState();
            this.fetchAndRender(this.currentHorizon);
        }

        onHorizonClick(event) {
            const btn = event.target.closest('.horizon-btn');
            if (!btn) return;
            const h = parseInt(btn.dataset.horizon, 10);
            if (!h) return;
            this.currentHorizon = h;
            $$('.horizon-btn').forEach(b => b.classList.toggle('active', b === btn));
            const label = $('#horizonLabel');
            if (label) label.textContent = String(h);
            // Drift snapshots are horizon-scoped — invalidate when horizon changes.
            if (this.driftCacheHorizon !== h) {
                this.driftSnapshots = null;
                this.driftCacheHorizon = null;
            }
            this.fetchAndRender(h).then(() => {
                if (this.driftEnabled) this.ensureDriftAndRender();
            });
        }

        async fetchDrift(horizon) {
            const card = document.getElementById('marketingProjectionCard');
            if (!card) return null;
            const url = card.dataset.historyUrl;
            if (!url) return null;
            const u = new URL(url, window.location.origin);
            u.searchParams.set('days', String(horizon));
            u.searchParams.set('lookback', '90');
            const data = await window.api(u.toString(), {
                headers: { 'Accept': 'application/json' },
                notifyError: false
            });
            return data.snapshots || [];
        }

        async ensureDriftAndRender() {
            if (this.driftSnapshots && this.driftCacheHorizon === this.currentHorizon) {
                if (this.currentData) this.renderChart(this.currentData);
                return;
            }
            try {
                const snaps = await this.fetchDrift(this.currentHorizon);
                this.driftSnapshots = snaps || [];
                this.driftCacheHorizon = this.currentHorizon;
                if (this.currentData) this.renderChart(this.currentData);
            } catch (err) {
                console.error('[MarketingDashboard] drift fetch failed', err);
                this.driftSnapshots = [];
                this.driftCacheHorizon = this.currentHorizon;
            }
        }

        onSnapshotClick(event) {
            const btn = event.target.closest('#projSnapshotBtn');
            if (!btn) return;
            const modalEl = document.getElementById('saveSnapshotModal');
            if (!modalEl || !window.bootstrap) return;
            const input = document.getElementById('saveSnapshotName');
            const errEl = document.getElementById('saveSnapshotError');
            const confirmBtn = document.getElementById('saveSnapshotConfirmBtn');
            if (input) input.value = '';
            if (errEl) { errEl.textContent = ''; errEl.classList.add('d-none'); }
            if (confirmBtn) confirmBtn.disabled = false;
            const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
            modal.show();
            if (input) setTimeout(() => input.focus(), 150);
        }

        async runSaveSnapshot() {
            const card = document.getElementById('marketingProjectionCard');
            const url = card && card.dataset.snapshotUrl;
            if (!url) return;
            const input = document.getElementById('saveSnapshotName');
            const errEl = document.getElementById('saveSnapshotError');
            const confirmBtn = document.getElementById('saveSnapshotConfirmBtn');
            const status = document.getElementById('projSnapshotStatus');
            const name = (input && input.value || '').trim();
            if (errEl) { errEl.textContent = ''; errEl.classList.add('d-none'); }
            if (confirmBtn) confirmBtn.disabled = true;
            if (status) status.textContent = 'Saving…';
            try {
                const data = await window.api(url, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json'
                    },
                    body: { name: name || null },
                    notifyError: false
                });
                const n = data.snapshots_written || 0;
                if (status) {
                    status.textContent = name
                        ? `Saved "${name}" (${n} channel${n === 1 ? '' : 's'})`
                        : `Saved ${n} channel snapshot${n === 1 ? '' : 's'}`;
                }
                const modalEl = document.getElementById('saveSnapshotModal');
                if (modalEl && window.bootstrap) {
                    window.bootstrap.Modal.getOrCreateInstance(modalEl).hide();
                }
                // Invalidate drift cache so next toggle/render picks up the new pin.
                this.driftSnapshots = null;
                this.driftCacheHorizon = null;
                if (this.driftEnabled) this.ensureDriftAndRender();
            } catch (err) {
                console.error('[MarketingDashboard] snapshot save failed', err);
                if (errEl) {
                    errEl.textContent = 'Save failed. Check console.';
                    errEl.classList.remove('d-none');
                }
                if (status) status.textContent = 'Save failed';
            } finally {
                if (confirmBtn) confirmBtn.disabled = false;
                if (status) {
                    setTimeout(() => { status.textContent = ''; }, 4000);
                }
            }
        }

        onSaveSnapshotConfirmClick(event) {
            if (!event.target.closest('#saveSnapshotConfirmBtn')) return;
            this.runSaveSnapshot();
        }

        onSignupsToggle(event) {
            const btn = event.target.closest('#projSignupsToggle');
            if (!btn) return;
            this.signupsVisible = !this.signupsVisible;
            btn.setAttribute('aria-pressed', String(this.signupsVisible));
            btn.classList.toggle('active', this.signupsVisible);
            const chart = window.TurboChart && window.TurboChart.get('marketingProjectionChart');
            if (chart) {
                const idx = chart.data.datasets.findIndex(d => d.label === 'Signups');
                if (idx >= 0) {
                    chart.setDatasetVisibility(idx, this.signupsVisible);
                    chart.update();
                }
            }
        }

        onDriftToggle(event) {
            const btn = event.target.closest('#projDriftToggle');
            if (!btn) return;
            this.driftEnabled = !this.driftEnabled;
            btn.setAttribute('aria-pressed', String(this.driftEnabled));
            btn.classList.toggle('active', this.driftEnabled);
            if (this.driftEnabled) {
                this.ensureDriftAndRender();
            } else if (this.currentData) {
                this.renderChart(this.currentData);
            }
        }

        async onCompareClick(event) {
            const btn = event.target.closest('#projCompareBtn');
            if (!btn) return;
            const diffUrl = btn.dataset.diffUrl;
            if (!diffUrl) return;
            const modalEl = document.getElementById('compareSnapshotsModal');
            if (!modalEl || !window.bootstrap) return;

            const fromSel = document.getElementById('compareFromSelect');
            const toSel = document.getElementById('compareToSelect');
            const formWrap = document.getElementById('compareForm');
            const noSnapWarn = document.getElementById('compareNoSnapshots');
            const errEl = document.getElementById('compareError');
            const confirmBtn = document.getElementById('compareConfirmBtn');
            if (errEl) { errEl.textContent = ''; errEl.classList.add('d-none'); }

            const status = document.getElementById('projSnapshotStatus');
            if (status) status.textContent = 'Loading snapshots…';
            btn.disabled = true;

            let snaps = [];
            try {
                snaps = await this.fetchDrift(this.currentHorizon) || [];
            } catch (err) {
                console.error('[MarketingDashboard] snapshot list fetch failed', err);
            } finally {
                btn.disabled = false;
                if (status) status.textContent = '';
            }

            const labelByDate = new Map();
            snaps.forEach(s => {
                if (s && s.edit_date && s.snapshot_label && !labelByDate.has(s.edit_date)) {
                    labelByDate.set(s.edit_date, s.snapshot_label);
                }
            });
            const dates = Array.from(new Set(snaps.map(s => s.edit_date).filter(Boolean))).sort();
            const enoughSnaps = dates.length >= 2;
            if (formWrap) formWrap.classList.toggle('d-none', !enoughSnaps);
            if (noSnapWarn) noSnapWarn.classList.toggle('d-none', enoughSnaps);
            if (confirmBtn) confirmBtn.disabled = !enoughSnaps;

            if (enoughSnaps && fromSel && toSel) {
                const escape = (s) => String(s).replace(/[&<>"']/g, c => (
                    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
                ));
                const opts = dates.map(d => {
                    const lbl = labelByDate.get(d);
                    const text = lbl ? `${d} — ${escape(lbl)}` : d;
                    return `<option value="${d}">${text}</option>`;
                }).join('');
                fromSel.innerHTML = opts;
                toSel.innerHTML = opts;
                fromSel.value = dates[0];
                toSel.value = dates[dates.length - 1];
            }

            const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
            modal.show();
        }

        async runCompareDiff() {
            const btn = document.getElementById('projCompareBtn');
            const diffUrl = btn && btn.dataset.diffUrl;
            if (!diffUrl) return;
            const fromSel = document.getElementById('compareFromSelect');
            const toSel = document.getElementById('compareToSelect');
            const errEl = document.getElementById('compareError');
            const confirmBtn = document.getElementById('compareConfirmBtn');
            const fromVal = fromSel && fromSel.value;
            const toVal = toSel && toSel.value;
            if (!fromVal || !toVal) return;
            if (fromVal === toVal) {
                if (errEl) {
                    errEl.textContent = 'Pick two different snapshots.';
                    errEl.classList.remove('d-none');
                }
                return;
            }
            const status = document.getElementById('projSnapshotStatus');
            if (confirmBtn) confirmBtn.disabled = true;
            if (status) status.textContent = 'Loading diff…';
            try {
                const url = `${diffUrl}?from=${encodeURIComponent(fromVal)}&to=${encodeURIComponent(toVal)}`;
                const env = await window.api(url, {
                    headers: { 'Accept': 'application/json' },
                    notifyError: false
                });
                const data = env && env.data ? env.data : env;
                const changes = (data && data.channel_changes) || [];
                const kpi = (data && data.kpi_deltas) || {};
                if (status) {
                    status.textContent =
                        `${changes.length} channel change(s), ${Object.keys(kpi).length} KPI delta(s)`;
                }
                console.info('[MarketingDashboard] period diff', data);
                const modalEl = document.getElementById('compareSnapshotsModal');
                if (modalEl && window.bootstrap) {
                    window.bootstrap.Modal.getOrCreateInstance(modalEl).hide();
                }
            } catch (err) {
                console.error('[MarketingDashboard] compare failed', err);
                if (errEl) {
                    errEl.textContent = 'Compare failed. Check console.';
                    errEl.classList.remove('d-none');
                }
                if (status) status.textContent = 'Compare failed';
            } finally {
                if (confirmBtn) confirmBtn.disabled = false;
                if (status) {
                    setTimeout(() => { status.textContent = ''; }, 6000);
                }
            }
        }

        onCompareConfirmClick(event) {
            if (!event.target.closest('#compareConfirmBtn')) return;
            this.runCompareDiff();
        }

        onPreviewToggle(event) {
            const input = event.target;
            if (!input.classList.contains('preview-toggle')) return;
            const code = input.dataset.channelCode;
            if (!code) return;
            if (input.checked) this.previewSelected.add(code);
            else this.previewSelected.delete(code);
            this.syncSelectAllState();
            this.refreshPreviewOverlay();
        }

        onSelectAllToggle(event) {
            const target = event.target;
            if (target.id !== 'previewSelectAll') return;
            const checked = target.checked;
            this.previewSelected.clear();
            $$('.preview-toggle').forEach(input => {
                input.checked = checked;
                if (checked) this.previewSelected.add(input.dataset.channelCode);
            });
            this.refreshPreviewOverlay();
        }

        syncSelectAllState() {
            const all = $$('.preview-toggle');
            const selectAll = $('#previewSelectAll');
            if (!selectAll || all.length === 0) return;
            const allChecked = Array.from(all).every(i => i.checked);
            selectAll.checked = allChecked;
        }

        // ---- growth-rate editor ---------------------------------------------

        onGrowthRateAction(e) {
            const btn = e.target.closest('[data-action]');
            if (!btn) return;
            const action = btn.dataset.action;
            if (action === 'edit-growth-rate') return this.showGrowthRateEditor();
            if (action === 'cancel-growth-rate') return this.hideGrowthRateEditor();
            if (action === 'save-growth-rate') return this.saveGrowthRate();
        }

        showGrowthRateEditor() {
            const editor = document.getElementById('growthRateEditor');
            const input = document.getElementById('growthRateInput');
            if (!editor || !input) return;
            editor.classList.remove('d-none');
            window.api('/admin/api/marketing/config', {
                notifyError: false
            })
                .then(data => {
                    const cur = data && data.marketing_daily_growth_rate;
                    input.value = (cur && cur.value !== null && cur.value !== '') ? cur.value : '';
                    input.focus();
                })
                .catch(() => { input.focus(); });
        }

        hideGrowthRateEditor() {
            const editor = document.getElementById('growthRateEditor');
            const status = document.getElementById('growthRateStatus');
            if (editor) editor.classList.add('d-none');
            if (status) status.textContent = '';
        }

        saveGrowthRate() {
            const input = document.getElementById('growthRateInput');
            const status = document.getElementById('growthRateStatus');
            if (!input) return;
            const raw = input.value.trim();
            status.textContent = 'Saving…';
            window.api('/admin/api/marketing/config', {
                method: 'POST',
                body: { marketing_daily_growth_rate: raw },
                notifyError: false
            })
                .then(() => {
                    status.textContent = 'Saved';
                    this.hideGrowthRateEditor();
                    this.fetchAndRender(this.currentHorizon);
                })
                .catch(err => {
                    status.textContent = 'Failed';
                    console.error('growth rate save failed', err);
                });
        }
}
